import { caseDbApiEndpoint } from '../utils/apiOrigin';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

const readResponse = async (response) => {
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.success === false) {
        const error = new Error(payload?.message || payload?.error || (
            [404, 503].includes(response.status)
                ? 'Field recommendations are not available in this environment yet.'
                : 'CaseDB could not complete the field recommendation request.'
        ));
        error.status = response.status;
        error.code = payload?.code || payload?.error_code || '';
        error.payload = payload;
        throw error;
    }
    return payload;
};

const request = async (action, payload, options = {}, method = 'POST') => {
    const endpoint = options.endpoint || caseDbApiEndpoint('field-recommendations.php');
    const init = { method, credentials: 'include', cache: 'no-store', signal: options.signal };
    let url = endpoint;
    if (method === 'GET') {
        url = new URL(endpoint, window.location.href);
        url.searchParams.set('action', action);
        Object.entries(payload).forEach(([key, value]) => {
            if (value !== '' && value !== undefined && value !== null) url.searchParams.set(key, String(value));
        });
        url = url.toString();
    } else {
        if (options.signal?.aborted) throw new DOMException('Request canceled', 'AbortError');
        const csrfToken = await ensureCaseDbCsrfToken();
        if (options.signal?.aborted) throw new DOMException('Request canceled', 'AbortError');
        if (!csrfToken) {
            const error = new Error('Your session could not be verified. Sign in again before changing recommendations.');
            error.status = 401;
            error.code = 'csrf_unavailable';
            throw error;
        }
        init.headers = withCsrfHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }, 'POST');
        init.body = JSON.stringify({ ...payload, action });
    }
    return readResponse(await fetch(url, init));
};

export const fieldRecommendationsApi = Object.freeze({
    catalog: (templateId, caseTypeId, options) => request('catalog', {
        template_id: templateId, case_type_id: caseTypeId,
    }, options, 'GET'),
    saveRule: (rule, options) => request('save_rule', { rule }, options),
    disableRule: (rule, options) => request('disable_rule', {
        id: rule.id, version: rule.version, template_id: rule.template_id, case_type_id: rule.case_type_id,
    }, options),
    preview: (payload, options) => request('preview', payload, options),
    accept: (payload, options) => request('accept', payload, options),
});

export const fieldRecommendationError = (error) => {
    if ([404, 503].includes(error?.status)) {
        return 'Field recommendations are not enabled in this environment yet. An administrator must finish the server setup before rules can be saved.';
    }
    if (error?.status === 409) {
        return 'This rule or its field options changed while you were editing. Reload the configuration and review the current mapping before saving again.';
    }
    return error?.message || 'Field recommendations are unavailable.';
};
